import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Divider, Switch, TextField, Typography, styled } from '@mui/material';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import CurrencySelector from 'further-ui/components/CurrencySelector';
import NumberInput from 'further-ui/components/NumberInput';
import BasicSelect from 'further-ui/components/BasicSelect';
import {
  ForeignCurrencyCostRateCalculationType,
  FxDataFeed,
  VehicleJurisdiction,
  CommitmentType,
  CapitalContributionRule,
  ReportingPeriods,
} from 'further-types/lp/vehicle';
import { AllocationRule } from 'further-types/lp/allocations';
import {
  AllocationRuleLabels,
  ForeignCurrencyCostRateCalculationLabels,
  VehicleJurisdictionLabels,
  CommitmentTypeLabels,
  CapitalContributionRuleLabels,
  ReportingPeriodsLabels,
} from 'further-ui/labels';

const fxDataFeedOptions = Object.values(FxDataFeed).map((feed) => ({
  value: feed,
  label: feed,
}));

const allocationRuleOptions = [
  {
    value: AllocationRule.ByCommitmentAndClosingDate,
    label: AllocationRuleLabels[AllocationRule.ByCommitmentAndClosingDate],
  },
];

const foreignCurrencyCostRateCalculationOptions = Object.values(
  ForeignCurrencyCostRateCalculationType,
).map((calculation) => ({
  value: calculation,
  label: ForeignCurrencyCostRateCalculationLabels[calculation],
}));

const vehicleJurisdictionOptions = Object.values(VehicleJurisdiction).map(
  (value: VehicleJurisdiction) => ({
    value,
    label: VehicleJurisdictionLabels[value],
  }),
);

const commitmentTypeOptions = Object.values(CommitmentType).map(
  (value: CommitmentType) => ({
    value,
    label: CommitmentTypeLabels[value],
  }),
);

const reportingPeriodOptions = Object.values(ReportingPeriods).map(
  (value: ReportingPeriods) => ({
    value,
    label: ReportingPeriodsLabels[value],
  }),
);

const capitalContributionRuleOptions = Object.values(
  CapitalContributionRule,
).map((value: CapitalContributionRule) => ({
  value,
  label: CapitalContributionRuleLabels[value],
}));

const StyledColumn = styled(Column)(({ theme }) => ({
  maxWidth: 600,
  paddingBottom: theme.spacing(theme.spacingSizes.xl),
}));

type CommitmentOption = {
  value: string;
  label: string;
};

type Props = {
  disableChangeOfCurrency: boolean;
  disableChangeOfFinancialYear: boolean;
  disableCommitmentTypeChange: boolean;
  commitmentOptions: Array<CommitmentOption>;
};

const NONE_OPTION_VALUE = 'none';

const GeneralInformation: FC<Props> = ({
  disableChangeOfCurrency,
  disableChangeOfFinancialYear,
  disableCommitmentTypeChange,
  commitmentOptions,
}) => {
  const { control, setValue, watch, clearErrors, trigger } = useFormContext();
  const commitmentType = watch('commitmentType');
  const capitalContributionRule = watch('capitalContributionRule');
  const baseCurrency = watch('baseCurrency');

  const handleCommitmentTypeChange = (
    value: CommitmentType,
    onChange: (value: CommitmentType) => void,
  ) => {
    onChange(value);
    if (value !== CommitmentType.CapitalAndLoan) {
      setValue('capitalContributionRule', null);
      setValue('capitalContributionPercentage', null);
      setValue('fixedCapitalAmount', null);
      clearErrors([
        'capitalContributionRule',
        'capitalContributionPercentage',
        'fixedCapitalAmount',
      ]);
      trigger([
        'capitalContributionRule',
        'capitalContributionPercentage',
        'fixedCapitalAmount',
      ]);
    }
  };

  return (
    <StyledColumn spacing="lg">
      <FormField label="Vehicle name" size="md">
        <Controller
          name="vehicleName"
          control={control}
          render={({ fieldState: { error }, field: { ref, ...rest } }) => (
            <TextField
              {...rest}
              inputRef={ref}
              placeholder="Enter vehicle name"
              error={!!error}
              helperText={error?.message?.toString()}
              fullWidth
              size="small"
              variant="outlined"
            />
          )}
        />
      </FormField>

      <FormField
        label="Commitment types"
        size="md"
        tooltip={
          disableCommitmentTypeChange
            ? 'You cannot change the commitment type after the first close.'
            : ''
        }
      >
        <Controller
          name="commitmentType"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <BasicSelect
              options={commitmentTypeOptions}
              value={value}
              onChange={(value) => handleCommitmentTypeChange(value, onChange)}
              error={!!error}
              helperText={error?.message?.toString()}
              placeholder="Select commitment types"
              fullWidth
              disabled={disableCommitmentTypeChange}
            />
          )}
        />
      </FormField>

      {commitmentType === CommitmentType.CapitalAndLoan && (
        <FormField
          label="Capital contribution rule"
          size="md"
          tooltip={
            disableCommitmentTypeChange
              ? 'You cannot change the capital contribution rule after the first close.'
              : ''
          }
        >
          <Column fullWidth spacing="sm">
            <Controller
              name="capitalContributionRule"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <BasicSelect
                  options={capitalContributionRuleOptions}
                  fullWidth
                  placeholder="Select capital contribution rule"
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                    setValue('capitalContributionPercentage', null);
                    setValue('fixedCapitalAmount', null);
                    clearErrors([
                      'capitalContributionPercentage',
                      'fixedCapitalAmount',
                    ]);
                  }}
                  disabled={disableCommitmentTypeChange}
                  error={!!error}
                  helperText={error?.message?.toString()}
                />
              )}
            />
            {capitalContributionRule ===
              CapitalContributionRule.FixedCommitmentPercentage && (
              <Controller
                name="capitalContributionPercentage"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <NumberInput
                    value={value}
                    onChange={onChange}
                    placeholder="Enter percentage"
                    min={0}
                    max={100}
                    decimals={10}
                    helperText={error?.message?.toString()}
                    showPercentSuffix
                    error={!!error}
                    disabled={disableCommitmentTypeChange}
                  />
                )}
              />
            )}
            {capitalContributionRule ===
              CapitalContributionRule.ProRataOfFixedPartnershipCapital && (
              <Controller
                name="fixedCapitalAmount"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <NumberInput
                    value={value}
                    onChange={onChange}
                    placeholder="Enter value"
                    min={0}
                    decimals={2}
                    helperText={error?.message?.toString()}
                    error={!!error}
                    currencyStartAdornment
                    currency={baseCurrency}
                    disabled={disableCommitmentTypeChange}
                  />
                )}
              />
            )}
          </Column>
        </FormField>
      )}

      <Divider />

      <FormField label="Vehicle jurisdiction" size="md">
        <Controller
          name="vehicleJurisdiction"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <BasicSelect
              options={vehicleJurisdictionOptions}
              value={value}
              placeholder="Select vehicle jurisdiction"
              onChange={(value) => onChange(value)}
              error={!!error}
              helperText={error?.message?.toString()}
              fullWidth
            />
          )}
        />
      </FormField>

      <FormField
        label={disableChangeOfCurrency ? 'Base currency' : 'Base currency*'}
        size="md"
        tooltip={
          disableChangeOfCurrency
            ? 'You cannot change the base currency once transactions have been added.'
            : ''
        }
      >
        <Controller
          name="baseCurrency"
          control={control}
          render={({ fieldState: { error }, field: { ref, ...rest } }) => (
            <CurrencySelector
              {...rest}
              placeholder="Select currency"
              error={error}
              disabled={disableChangeOfCurrency}
            />
          )}
        />
      </FormField>

      <FormField
        label="Financial year end"
        size="md"
        tooltip={
          disableChangeOfFinancialYear
            ? 'You cannot change the financial year end once fees have been approved.'
            : ''
        }
      >
        <Row fullWidth spacing="sm">
          <Controller
            name="financialYearEndDay"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <NumberInput
                value={value}
                onChange={onChange}
                min={1}
                max={31}
                placeholder="Enter date"
                fullWidth
                helperText={error?.message?.toString()}
                error={!!error}
                disabled={disableChangeOfFinancialYear}
              />
            )}
          />
          <Controller
            name="financialYearEndMonth"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <BasicSelect
                options={[
                  { value: '1', label: 'January' },
                  { value: '2', label: 'February' },
                  { value: '3', label: 'March' },
                  { value: '4', label: 'April' },
                  { value: '5', label: 'May' },
                  { value: '6', label: 'June' },
                  { value: '7', label: 'July' },
                  { value: '8', label: 'August' },
                  { value: '9', label: 'September' },
                  { value: '10', label: 'October' },
                  { value: '11', label: 'November' },
                  { value: '12', label: 'December' },
                ]}
                fullWidth
                error={!!error}
                helperText={error?.message?.toString()}
                value={value != null ? String(value) : ''}
                placeholder="Select month"
                onChange={(value) => onChange(Number(value))}
                disabled={disableChangeOfFinancialYear}
              />
            )}
          />
        </Row>
      </FormField>

      <FormField label="Currency feed" size="md">
        <Controller
          name="fxDataFeed"
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <BasicSelect
              options={fxDataFeedOptions}
              fullWidth
              error={!!error}
              helperText={error?.message?.toString()}
              placeholder="Select currency feed"
              value={value}
              onChange={(value) => onChange(value)}
            />
          )}
        />
      </FormField>

      <Divider />

      <FormField label="Reporting periods" size="md">
        <Controller
          name="reportingPeriods"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <BasicSelect
              options={reportingPeriodOptions}
              placeholder="Select reporting periods"
              value={value}
              onChange={(value) => onChange(value)}
              error={!!error}
              helperText={error?.message?.toString()}
              fullWidth
            />
          )}
        />
      </FormField>

      <FormField
        label="Reporting deadline"
        size="md"
        tooltip="ILPA guidelines recommend that draft unaudited financials are typically delivered to investors 45–60 days after quarter-end. Fund of funds vehicles may take up to 75–90 days."
      >
        <Controller
          name="reportingDeadlineDays"
          control={control}
          defaultValue={45}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Row centered spacing="sm">
              <NumberInput
                value={value}
                onChange={onChange}
                min={1}
                max={90}
                placeholder="Enter days"
                helperText={error?.message?.toString()}
                error={!!error}
              />
              <Typography variant="body2"> days after period end</Typography>
            </Row>
          )}
        />
      </FormField>

      <Divider />

      <FormField label="Default allocation rule" size="md">
        <Controller
          name="defaultAllocationRule"
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <BasicSelect
              options={allocationRuleOptions}
              placeholder="Select default allocation rule"
              fullWidth
              error={!!error}
              helperText={error?.message?.toString()}
              value={value}
              onChange={(value) => onChange(value)}
            />
          )}
        />
      </FormField>
      <FormField label="Foreign currency cost rate calculation" size="md">
        <Controller
          name="foreignCurrencyCostRateCalculation"
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <BasicSelect
              options={foreignCurrencyCostRateCalculationOptions}
              fullWidth
              error={!!error}
              helperText={error?.message?.toString()}
              value={value}
              placeholder="Select foreign currency cost rate calculation"
              onChange={(value) => onChange(value)}
            />
          )}
        />
      </FormField>
      <FormField
        label="Exclude general partner from vehicle allocations"
        size="md"
        tooltip="Turn this on when the general partner entity's fund commitment is to be excluded from the vehicle's total commitment and all transaction allocations."
      >
        <Controller
          name="excludeGeneralPartnerFromAllocations"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch
              edge="start"
              checked={value ?? false}
              onChange={(e) => onChange(e.target.checked)}
            />
          )}
        />
      </FormField>
      <FormField
        label="Balancing entity"
        size="md"
        tooltip="Where currency transaction allocations are rounded to two decimal places, there may be stray cents/pennies that need allocating. By default, the system adds/deducts one cent/penny from each investor in order from largest to smallest. Setting a balancing entity will instead put these adjustments into the balancing entity only."
      >
        <Controller
          name="balancingEntityId"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <BasicSelect
              options={[
                { value: NONE_OPTION_VALUE, label: 'None' },
                ...commitmentOptions,
              ]}
              value={value ?? NONE_OPTION_VALUE}
              onChange={(selected) =>
                onChange(selected === NONE_OPTION_VALUE ? null : selected)
              }
              placeholder="Select balancing entity"
              error={!!error}
              helperText={error?.message?.toString()}
              fullWidth
            />
          )}
        />
      </FormField>
    </StyledColumn>
  );
};

export default GeneralInformation;
